import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import '../../styles/TrainingView.css';
import StreakUpgradeButton from '../../components/StreakUpgradeButton';
import LockedTrainingModal from '../../components/modals/LockedTrainingModal';
import FreeTrainingsCompletedModal from '../../components/modals/FreeTrainingsCompletedModal';
import PricingModal from '../../components/modals/PricingModal';
import FreeMockExamCompletedModal from '../../components/modals/FreeMockExamCompletedModal';
import PremiumMockExamCompletedModal from '../../components/modals/PremiumMockExamCompletedModal';
import { API_URL } from '../../app/constants'

const PracticeMode = () => {
  const [selectedCategory, setSelectedCategory] = useState('vocabulary');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [practiceExams, setPracticeExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showFreeTrainingsCompletedModal, setShowFreeTrainingsCompletedModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showFreeMockExamCompletedModal, setShowFreeMockExamCompletedModal] = useState(false);
  const [showPremiumMockExamCompletedModal, setShowPremiumMockExamCompletedModal] = useState(false);
  const [mockExamCompleted, setMockExamCompleted] = useState(false);
  const [showMockExamIntroModal, setShowMockExamIntroModal] = useState(false);
  const [pendingMockExamLevel, setPendingMockExamLevel] = useState(null);
  const navigate = useNavigate();
  
  const categories = [
    { value: 'vocabulary', label: 'Vocabulary' },
    { value: 'grammar', label: 'Grammar' },
    { value: 'reading', label: 'Reading' },
    { value: 'mock-exam', label: 'Mock Exam' }
  ];

  // Check user status and mock exam completion
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const jwt = localStorage.getItem('jwt');
        const response = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: { 'Authorization': `Bearer ${jwt}` },
          method: "GET",
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUserStatus(userData.status);
          
          // Check if user has completed their free mock exam
          try {
            const mockExamResponse = await fetch(`${API_URL}/api/v1/mock-exam/analytics`, {
              headers: { 'Authorization': `Bearer ${jwt}` }
            });
            
            if (mockExamResponse.ok) {
              // If we get 200 response, it means they've completed at least one mock exam
              const mockExamData = await mockExamResponse.json();
              console.log('User has completed mock exam:', mockExamData);
              setMockExamCompleted(true);
            } else if (mockExamResponse.status === 404) {
              // 404 means no completed mock exams found - user hasn't completed free mock exam yet
              console.log('User has not completed any mock exams yet');
              setMockExamCompleted(false);
            } else {
              // Other errors - assume no mock exam completed
              console.log('Error checking mock exam status, assuming not completed');
              setMockExamCompleted(false);
            }
          } catch (error) {
            console.log('Error fetching mock exam data - assuming user has not completed free mock exam yet:', error);
            setMockExamCompleted(false);
          }
        }
      } catch (error) {
        console.error("Error checking user status:", error);
      }
    };
    
    checkUserStatus();
  }, []);

  // Fetch practice exams for selected category
  useEffect(() => {
    const fetchPracticeExams = async () => {
      if (!userStatus) return;
      
      // Only show loading if we don't have data yet
      if (practiceExams.length === 0) {
        setLoading(true);
      }
      setError(null);
      
      try {
        if (selectedCategory === 'mock-exam') {
          // Handle mock exam category
          const mockExamData = {
            id: 'mock-exam-1',
            title: 'Full Mock Exam',
            description: 'Complete mock exam with all sections',
            completed: mockExamCompleted
          };
          setPracticeExams([mockExamData]);
        } else {
          const jwt = localStorage.getItem('jwt');
          
          const response = await fetch(`${API_URL}/api/v1/panel/practice-exams/${selectedCategory}`, {
            headers: { 'Authorization': `Bearer ${jwt}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setPracticeExams(data);
          } else {
            setError("Error loading practice exams");
          }
        }
      } catch (error) {
        console.error("Error fetching practice exams:", error);
        setError("Error loading practice exams");
      } finally {
        setLoading(false);
      }
    };

    fetchPracticeExams();
  }, [selectedCategory, userStatus, mockExamCompleted, practiceExams.length]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  const handleUpgradeClick = () => {
    setShowFreeTrainingsCompletedModal(false);
    setShowPricingModal(true);
  };

  const getExamType = (exam, index) => {
    if (selectedCategory === 'mock-exam') {
      return 'Mock Exam';
    }
    return selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1);
  };

  const getProgress = (exam) => {
    if (exam.completed) return 100;
    return 0;
  };

  const isExamLocked = (index) => {
    // First 3 exams are free, rest are locked for non-premium users
    return userStatus !== 'active' && index >= 3;
  };

  const handleExamClick = (e, exam, index) => {
    if (selectedCategory === 'mock-exam') {
      e.preventDefault();
      handleMockExamClick(exam);
      return false;
    }
    if (isExamLocked(index)) {
      e.preventDefault();
      setShowFreeTrainingsCompletedModal(true);
      return false;
    }
  };

  const handleMockExamClick = async (exam) => {
    console.log('Mock exam card clicked');
    console.log('mockExamCompleted:', mockExamCompleted);
    console.log('userStatus:', userStatus);
    
    // Check if user has already completed their mock exam
    if (mockExamCompleted) {
      if (userStatus === 'active') {
        console.log('Showing premium completed modal');
        setShowPremiumMockExamCompletedModal(true);
        return;
      } else {
        console.log('Showing free completed modal');
        setShowFreeMockExamCompletedModal(true);
        return;
      }
    }
    
    console.log('Proceeding to mock exam');
    
    try {
      const jwt = localStorage.getItem('jwt');
      const userResponse = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
        method: "GET",
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        const targetLevel = userData.target_level || userData.level || 'N5';
        setPendingMockExamLevel(targetLevel);
        setShowMockExamIntroModal(true);
      } else {
        setPendingMockExamLevel('N5');
        setShowMockExamIntroModal(true);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setPendingMockExamLevel('N5');
      setShowMockExamIntroModal(true);
    }
  };

  const handleLockClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFreeTrainingsCompletedModal(true);
  };

  const getExamPath = (exam) => {
    if (selectedCategory === 'mock-exam') {
      return '#'; // Will be handled by onClick
    }
    if (exam.completed) {
      return `/review-personalized/${exam.exam_id}`;
    }
    return `/personalized-exam/${exam.exam_id}`;
  };

  return (
    <div className="training-view">
      <StreakUpgradeButton source="Practice Mode" />
      <div className="dashboard-header">
        <h1>Practice Mode</h1>
      </div>
      
      <div className="session-selector">
        <button 
          className="session-button"
          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
        >
          {categories.find(cat => cat.value === selectedCategory)?.label || "Select Category"}
          <svg className={`arrow-icon ${showCategoryDropdown ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className={`session-dropdown ${showCategoryDropdown ? 'show' : ''}`}>
          {categories.map((category) => (
            <button
              key={category.value}
              className={`session-option ${selectedCategory === category.value ? 'active' : ''}`}
              onClick={() => handleCategorySelect(category.value)}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner-ripple">
            <div></div>
            <div></div>
          </div>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <div className="exercises-grid">
          {practiceExams.map((exam, index) => (
            <Link 
              key={exam.exam_id || index}
              onClick={(e) => handleExamClick(e, exam, index)}
              to={getExamPath(exam)}
              className={`exercise-card ${isExamLocked(index) ? 'locked' : ''}`}
              style={{ position: 'relative' }}
            >
              <div className="exercise-card-content">
                <div className="exercise-info">
                  <span className="exercise-type">{getExamType(exam, index)}</span>
                  <h3 className="exercise-title" style={{
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {exam.categories ? 
                      exam.categories.split(';').join(', ') 
                      : `Practice ${index + 1}`
                    }
                  </h3>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${getProgress(exam)}%` }}
                  />
                </div>
              </div>
              {isExamLocked(index) && userStatus !== 'active' && (
                <button 
                  className="lock-icon-button"
                  onClick={(e) => handleLockClick(e, index)}
                  aria-label="Upgrade to access this exam"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    color: '#6B7280'
                  }}
                >
                  <Lock size={14} />
                </button>
              )}
            </Link>
          ))}
          
          {practiceExams.length === 0 && !loading && (
            <div className="empty-state">No practice exams available for this category</div>
          )}
        </div>
      )}

      <LockedTrainingModal 
        isOpen={showLockedModal}
        onClose={() => setShowLockedModal(false)}
      />

      <FreeTrainingsCompletedModal 
        isOpen={showFreeTrainingsCompletedModal}
        onClose={() => setShowFreeTrainingsCompletedModal(false)}
        onUpgrade={handleUpgradeClick}
        source="Practice Mode"
      />

      <FreeMockExamCompletedModal 
        isOpen={showFreeMockExamCompletedModal}
        onClose={() => setShowFreeMockExamCompletedModal(false)}
        onUpgrade={() => setShowPricingModal(true)}
        source="Practice Mode - Free Mock Exam"
      />

      <PremiumMockExamCompletedModal 
        isOpen={showPremiumMockExamCompletedModal}
        onClose={() => setShowPremiumMockExamCompletedModal(false)}
      />

      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        source="Practice Mode - Mock Exam Upgrade"
      />

      {/* Mock Exam Introduction Modal */}
      {showMockExamIntroModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }} onClick={() => setShowMockExamIntroModal(false)}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px 28px',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              color: "#111827", 
              marginBottom: "16px",
              fontSize: '22px',
              fontWeight: '700',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Full Mock Exam
            </h2>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '16px',
              lineHeight: '1.5',
              color: '#6B7280',
              fontFamily: 'Manrope, sans-serif',
              textAlign: 'center'
            }}>
              This exam replicates the structure of the official JLPT exam (without the listening section) and will take around 50 min to complete. Good luck!
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column'
            }}>
              <button
                style={{
                  backgroundColor: "#9334E9",
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onClick={() => {
                  setShowMockExamIntroModal(false);
                  navigate(`/mock-exam/${pendingMockExamLevel}`);
                }}
              >
                Start Mock Exam
              </button>
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onClick={() => setShowMockExamIntroModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeMode;